import React, { useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { getClientColor, getClientInitials } from "../../utils/clientColors";

// Formattazione italiana: 1.234 € (solo migliaia, senza decimali)
function formatCurrencyShort(value) {
  const num = Number(value || 0);
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  const intPart = Math.round(abs).toString();
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${groupedInt} €`;
}

const RISK_THRESHOLD = 50; // Soglia rischio concentrazione: 50%

export default function ClientDistributionChart({ invoices, selectedYear, darkMode = false }) {
  // Calcola fatturato per cliente (basato su issueDate)
  const { clients, total, pieData } = useMemo(() => {
    const clientMap = {};

    invoices.forEach((inv) => {
      if (!inv.issueDate) return;
      const year = new Date(inv.issueDate).getFullYear();
      if (year !== selectedYear) return;

      const client = (inv.clientName || "Sconosciuto").trim();
      const amount = Number(inv.grossAmount) || 0;

      if (!clientMap[client]) {
        clientMap[client] = { name: client, amount: 0, count: 0 };
      }
      clientMap[client].amount += amount;
      clientMap[client].count += 1;
    });

    // Converti in array e ordina per importo decrescente
    const sorted = Object.values(clientMap).sort((a, b) => b.amount - a.amount);

    // Calcola totale
    const totalAmount = sorted.reduce((sum, c) => sum + c.amount, 0);

    // Assegna colori basati sul NOME del cliente (consistente in tutta l'app)
    const withColors = sorted.map((c, i) => {
      const color = getClientColor(c.name);
      return {
        ...c,
        index: i,
        percent: totalAmount > 0 ? (c.amount / totalAmount) * 100 : 0,
        isRisk: totalAmount > 0 && (c.amount / totalAmount) * 100 >= RISK_THRESHOLD,
        colorHex: color.hex,
        colorBg: color.bg,
      };
    });

    // Prepara dati per la torta (max 8 clienti + "Altri")
    let pie = [];
    if (withColors.length === 0) {
      pie = [];
    } else if (withColors.length <= 8) {
      pie = withColors;
    } else {
      const top8 = withColors.slice(0, 8);
      const others = withColors.slice(8);
      const othersTotal = others.reduce((sum, c) => sum + c.amount, 0);
      const othersPercent = totalAmount > 0 ? (othersTotal / totalAmount) * 100 : 0;

      pie = [
        ...top8,
        {
          name: `Altri (${others.length})`,
          amount: othersTotal,
          percent: othersPercent,
          count: others.reduce((sum, c) => sum + c.count, 0),
          colorHex: "#A3A3A3",
          colorBg: "bg-neutral-400",
          isRisk: false,
        },
      ];
    }

    return { clients: withColors, total: totalAmount, pieData: pie };
  }, [invoices, selectedYear]);

  // Calcola i segmenti della torta (angoli)
  const pieSegments = useMemo(() => {
    if (pieData.length === 0) return [];

    let cumulative = 0;
    return pieData.map((segment) => {
      const startAngle = cumulative;
      const angle = (segment.percent / 100) * 360;
      cumulative += angle;
      return { ...segment, startAngle, angle };
    });
  }, [pieData]);

  // Genera path SVG per un segmento della torta
  const createPieSlice = (startAngle, angle, radius = 90, cx = 100, cy = 100) => {
    if (angle >= 360) {
      return `M ${cx} ${cy - radius} A ${radius} ${radius} 0 1 1 ${cx - 0.01} ${cy - radius} Z`;
    }

    const startRad = ((startAngle - 90) * Math.PI) / 180;
    const endRad = ((startAngle + angle - 90) * Math.PI) / 180;

    const x1 = cx + radius * Math.cos(startRad);
    const y1 = cy + radius * Math.sin(startRad);
    const x2 = cx + radius * Math.cos(endRad);
    const y2 = cy + radius * Math.sin(endRad);

    const largeArc = angle > 180 ? 1 : 0;

    return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  // Check per clienti a rischio
  const riskClients = clients.filter((c) => c.isRisk);

  if (clients.length === 0) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] text-sm ${
        darkMode ? "text-neutral-500" : "text-neutral-600"
      }`}>
        Nessun dato per {selectedYear}. Inserisci fatture per vedere la distribuzione.
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 min-h-[400px]">
      {/* Colonna sinistra: Torta (45%) */}
      <div className="lg:w-[45%] flex flex-col items-center justify-center">
        {/* Torta grande */}
        <svg viewBox="0 0 200 200" className="w-64 h-64">
          {pieSegments.map((segment, i) => (
            <path
              key={i}
              d={createPieSlice(segment.startAngle, segment.angle)}
              fill={segment.colorHex}
              stroke={darkMode ? "#1A1A1A" : "white"}
              strokeWidth="2"
              className="transition-opacity hover:opacity-80 cursor-pointer"
            >
              <title>{segment.name}: {formatCurrencyShort(segment.amount)} ({segment.percent.toFixed(1)}%)</title>
            </path>
          ))}
          {/* Centro per effetto donut */}
          <circle cx="100" cy="100" r="45" className={darkMode ? "fill-neutral-900" : "fill-white"} />
          {/* Testo centrale */}
          <text
            x="100"
            y="92"
            textAnchor="middle"
            className={`text-[10px] ${darkMode ? "fill-neutral-500" : "fill-neutral-500"}`}
          >
            Totale
          </text>
          <text
            x="100"
            y="110"
            textAnchor="middle"
            className={`text-sm font-bold ${darkMode ? "fill-white" : "fill-neutral-900"}`}
          >
            {formatCurrencyShort(total)}
          </text>
        </svg>

        {/* Mini legenda sotto la torta */}
        <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-2 max-w-[280px]">
          {pieData.slice(0, 6).map((c, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs">
              <div
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: c.colorHex }}
              />
              <span className={`truncate max-w-[80px] ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
                {c.name}
              </span>
            </div>
          ))}
          {pieData.length > 6 && (
            <span className={`text-xs ${darkMode ? "text-neutral-600" : "text-neutral-500"}`}>
              +{pieData.length - 6} altri
            </span>
          )}
        </div>
      </div>

      {/* Colonna destra: Lista clienti (55%) */}
      <div className="lg:w-[55%] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <h3 className={`text-sm font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
            Peso clienti
          </h3>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            darkMode ? "bg-neutral-800 text-neutral-500" : "bg-neutral-100 text-neutral-600"
          }`}>
            Soglia: {RISK_THRESHOLD}%
          </span>
        </div>

        {/* Warning se ci sono clienti a rischio */}
        {riskClients.length > 0 && (
          <div
            className={`mb-4 p-3 rounded-lg flex items-start gap-2 ${
              darkMode ? "bg-amber-900/20" : "bg-amber-50"
            }`}
          >
            <AlertTriangle
              className={`w-4 h-4 mt-0.5 shrink-0 ${
                darkMode ? "text-amber-400" : "text-amber-600"
              }`}
            />
            <p className={`text-xs ${darkMode ? "text-amber-300" : "text-amber-800"}`}>
              <strong>{riskClients.length} cliente{riskClients.length > 1 ? "i" : ""}</strong>{" "}
              supera{riskClients.length > 1 ? "no" : ""} il {RISK_THRESHOLD}% del fatturato.
              Rischio concentrazione!
            </p>
          </div>
        )}

        {/* Lista clienti con avatar - COLORE SINCRONIZZATO CON TORTA */}
        <div className="flex-1 space-y-3 overflow-y-auto">
          {clients.slice(0, 8).map((client) => (
            <div
              key={client.name}
              className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                darkMode ? "hover:bg-neutral-800/50" : "hover:bg-neutral-50"
              }`}
            >
              {/* Avatar - usa lo stesso colore della torta basato sull'indice */}
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0 ${client.colorBg}`}
              >
                {getClientInitials(client.name)}
              </div>

              {/* Info cliente */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span
                    className={`text-sm font-medium truncate ${
                      client.isRisk
                        ? darkMode
                          ? "text-amber-400"
                          : "text-amber-700"
                        : darkMode
                        ? "text-white"
                        : "text-neutral-900"
                    }`}
                  >
                    {client.name}
                    {client.isRisk && (
                      <AlertTriangle
                        className={`inline w-3.5 h-3.5 ml-1 ${
                          darkMode ? "text-amber-400" : "text-amber-500"
                        }`}
                      />
                    )}
                  </span>
                  <span
                    className={`text-sm font-semibold shrink-0 ${
                      client.isRisk
                        ? darkMode
                          ? "text-amber-400"
                          : "text-amber-700"
                        : darkMode
                        ? "text-white"
                        : "text-neutral-900"
                    }`}
                  >
                    {client.percent.toFixed(1)}%
                  </span>
                </div>
                {/* Barra di progresso - usa il colore del cliente */}
                <div className="mt-1.5 flex items-center gap-2">
                  <div
                    className={`flex-1 h-1.5 rounded-full overflow-hidden ${
                      darkMode ? "bg-neutral-800" : "bg-neutral-100"
                    }`}
                  >
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.min(client.percent, 100)}%`,
                        backgroundColor: client.isRisk ? "#f59e0b" : client.colorHex,
                      }}
                    />
                  </div>
                  <span
                    className={`text-xs shrink-0 ${
                      darkMode ? "text-neutral-600" : "text-neutral-500"
                    }`}
                  >
                    {formatCurrencyShort(client.amount)}
                  </span>
                </div>
              </div>
            </div>
          ))}
          {clients.length > 8 && (
            <p className={`text-xs py-2 ${darkMode ? "text-neutral-600" : "text-neutral-500"}`}>
              +{clients.length - 8} altri clienti
            </p>
          )}
        </div>

        {/* Footer: Totale */}
        <div
          className={`mt-4 pt-3 border-t flex items-center justify-between ${
            darkMode ? "border-neutral-800" : "border-neutral-100"
          }`}
        >
          <span className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
            Totale fatturato
          </span>
          <span className={`text-base font-bold ${darkMode ? "text-white" : "text-neutral-900"}`}>
            {formatCurrencyShort(total)}
          </span>
        </div>
      </div>
    </div>
  );
}
